import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from 'react';
import { Card, CardBody, Row, Col } from 'reactstrap';
import { TrendingUp } from 'react-feather';
import axios from 'axios';
import { toast } from 'react-toastify';
import Chart from 'react-apexcharts';
import DatePicker from 'react-datepicker';
import AsyncSelect from 'react-select/async';
import Loader from 'react-loader-spinner';

const SitesGraph = forwardRef(({ orgName }, ref) => {
  const [siteData, setSiteData] = useState([]);
  const [sitegroupId, setSitegroupId] = useState('');
  const [graphData, setGraphData] = useState({ days: [], uploads_count: [] });
  const [sitename, setSitename] = useState('none');
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState({});
  const [series, setSeries] = useState([]);

  const today = new Date(Date.now());
  const minimumSelection = new Date(Date.now() - 180 * 24 * 60 * 60 * 1000);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date(Date.now()));
  const [siteNamesDropdownOptions, setSiteNameDropdownOptions] = useState([]);
  const [selectedSite, setSelectedSite] = useState({ value: '', label: 'Select a site…' });

  const onDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleSiteName = (e) => {
    setSelectedSite(e);
    setSitename(e['value']);
  };

  function getFormattedDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  }

  function parseDate(str) {
    str = getFormattedDate(str);
    const mdy = str.split('/');
    return new Date(mdy[2], mdy[0] - 1, mdy[1]);
  }

  function datediff(first, second) {
    return Math.round((second - first) / (1000 * 60 * 60 * 24));
  }

  const handleDataRequest = () => {
    const days = datediff(parseDate(startDate), parseDate(endDate));
    if (days > 30) {
      toast.error('Please select a date range within 30 days');
      return;
    }
    if (sitename === 'none') {
      toast.error('Please select a site first');
      return;
    }
    setLoading(true);
    axios
      .get(
        `/api/v1/organizations/${orgName}/sites/uploads_count?from_date=${getFormattedDate(startDate)}&to_date=${getFormattedDate(endDate)}&site_id=${sitename}&sitegroup_id=${sitegroupId}&page_type=client&controller_name=sites`
      )
      .then((res) => {
        setGraphData({
          days: res.data.sites_upload_count.days,
          uploads_count: res.data.sites_upload_count.uploads_count,
        });
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    getSiteUploadCount();
    getSiteNames();
  }, [orgName]);

  useImperativeHandle(ref, () => ({
    fetchSitesGraphData(id) {
      setSitegroupId(id);
      getSiteUploadCount(id);
      getSiteNames(id);
    },
  }));

  function getSiteNames(id = '') {
    setLoading(true);
    const url = id
      ? `/api/v1/organizations/${orgName}/site_name_list?sitegroup_id=${id}&page_type=client&controller_name=sites`
      : `/api/v1/organizations/${orgName}/site_name_list?page_type=client&controller_name=sites`;
    axios
      .get(url)
      .then((res) => {
        setSiteData(res.data.site_names);
        setSiteNameDropdownOptions(convertArray(res?.data?.site_names));
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
        setLoading(false);
      });
  }

  useEffect(() => {
    if ('days' in graphData && graphData?.days?.length > 0) {
      setOptions({
        chart: { id: 'sites-uploads', toolbar: { show: false } },
        xaxis: { categories: graphData.days, labels: { style: { fontSize: '11px', colors: '#6c757d' } } },
        yaxis: { labels: { style: { fontSize: '11px', colors: '#6c757d' } } },
        stroke: { curve: 'smooth', width: 2 },
        colors: ['#0d6efd'],
        grid: { borderColor: '#f1f3f5' },
        dataLabels: { enabled: false },
      });
      setSeries([{ name: 'Uploads', data: graphData.uploads_count }]);
    } else {
      setOptions({});
      setSeries([]);
    }
  }, [graphData]);

  function getSiteUploadCount(id = '', name = 'none') {
    if (!orgName) return;
    let url;
    if (id !== '' && name !== 'none') {
      url = `/api/v1/organizations/${orgName}/sites/uploads_count?from_date=${getFormattedDate(startDate)}&to_date=${getFormattedDate(endDate)}&site_id=${name}&sitegroup_id=${id}&page_type=client&controller_name=sites`;
    } else if (name !== 'none' && id === '') {
      url = `/api/v1/organizations/${orgName}/sites/uploads_count?from_date=${getFormattedDate(startDate)}&to_date=${getFormattedDate(endDate)}&site_id=${name}&page_type=client&controller_name=sites`;
    } else if (id !== '') {
      url = `/api/v1/organizations/${orgName}/sites/uploads_count?sitegroup_id=${id}&page_type=client&controller_name=sites`;
    } else {
      url = `/api/v1/organizations/${orgName}/sites/uploads_count?page_type=client&controller_name=sites`;
    }
    setLoading(true);
    axios
      .get(url)
      .then((res) => {
        setGraphData({
          days: res.data.sites_upload_count.days,
          uploads_count: res.data.sites_upload_count.uploads_count,
        });
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
        setLoading(false);
      });
  }

  function convertArray(inputArray) {
    return (inputArray || []).map(({ sitename, id }) => ({ value: id, label: sitename }));
  }

  const filterValues = (inputValue) =>
    siteNamesDropdownOptions.filter((i) => i.label.toLowerCase().includes(inputValue.toLowerCase()));

  const loadOptions = (inputValue, callback) => {
    setTimeout(() => {
      callback(filterValues(inputValue));
    }, 250);
  };

  const labelStyle = {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#6c757d',
  };

  const hasData = graphData?.days?.length > 0;

  return (
    <Card className='shadow-sm border-0 mb-3'>
      <CardBody style={{ padding: 24 }}>
        {/* Header */}
        <div className='d-flex align-items-start mb-3'>
          <div
            className='d-inline-flex align-items-center justify-content-center me-3 flex-shrink-0'
            style={{ width: 40, height: 40, borderRadius: 10, background: '#19875415', color: '#198754' }}
          >
            <TrendingUp size={20} />
          </div>
          <div>
            <h6 className='fw-semibold mb-1 text-dark' style={{ fontSize: 16 }}>
              Site Upload Trend
            </h6>
            <p className='text-muted mb-0' style={{ fontSize: 13 }}>
              Pick a site and date range (≤ 30 days, last 6 months) to chart its uploads.
            </p>
          </div>
        </div>

        {/* Controls */}
        <Row className='g-3 pt-3 pb-1' style={{ borderTop: '1px solid #f1f3f5' }}>
          <Col md={4}>
            <label className='d-block mb-1' style={labelStyle}>Site</label>
            <AsyncSelect
              name='site_name'
              value={selectedSite}
              placeholder='Select a site…'
              cacheOptions
              defaultOptions={siteNamesDropdownOptions}
              onChange={(res) => handleSiteName(res)}
              loadOptions={loadOptions}
            />
          </Col>
          <Col md={5}>
            <label className='d-block mb-1' style={labelStyle}>Date Range</label>
            <DatePicker
              selected={startDate}
              onChange={onDateChange}
              maxDate={today}
              minDate={minimumSelection}
              className='form-control form-control-sm'
              startDate={startDate}
              endDate={endDate}
              selectsRange
            />
            <span className='text-muted d-block mt-1' style={{ fontSize: 11 }}>
              Any 30 days within the last 6 months.
            </span>
          </Col>
          <Col md={3} className='d-flex align-items-end'>
            <button className='btn btn-primary w-100' onClick={handleDataRequest}>
              Apply
            </button>
          </Col>
        </Row>

        {/* Chart */}
        <div className='mt-4'>
          {loading ? (
            <div className='d-flex justify-content-center py-5'>
              <Loader type='Puff' color='#0d6efd' height={50} width={50} />
            </div>
          ) : hasData ? (
            <Chart options={options} series={series} height={360} type='line' />
          ) : (
            <div className='d-flex flex-column align-items-center justify-content-center text-muted py-5'>
              <TrendingUp size={28} className='mb-2 opacity-50' />
              <p className='mb-0' style={{ fontSize: 13 }}>
                Select a site and click Apply to see its upload trend.
              </p>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
});

export { SitesGraph as default };
